import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
import { convertHTMLToRichText, convertRichTextToHTML, isEmptyRichTextString, RichTextViewer } from 'seagull';
import { embedVariablePlaceholdersInDelta, extractVariableLabelsFromDelta } from '~/components/AgreementStudioPage/TemplateVariables/TemplateVariables.utils';
import { useAcceptServiceDescription } from '~/hooks/reactQuery/mutations/useAcceptServiceDescription';
import { useGenerateServiceDescription } from '~/hooks/reactQuery/mutations/useGenerateServiceDescription';
import { GenerateServiceDescriptionResponseType, GenerateServiceDescriptionType } from '~/hooks/reactQuery/mutations/useGenerateServiceDescription.types';
import { useRejectServiceDescription } from '~/hooks/reactQuery/mutations/useRejectServiceDescription';
import { useUndoServiceDescription } from '~/hooks/reactQuery/mutations/useUndoServiceDescription';
import { usePromptSuggestions } from '~/hooks/reactQuery/queries/usePromptSuggestions';
import { useLocaleKeys } from '~/i18n';
import { useStoreActions } from '~/store/store-context';
import FeedbackModal from '../FeedbackModal/FeedbackModal';
import HelpMeWriteDrawer from '../HelpMeWriteDrawer';
import { PresetType } from '../HelpMeWriteTextField/HelpMeWriteTextField.types';
import { SuggestedOutputDisplayTestIds } from '../SuggestedOutputDisplay/SuggestedOutputDisplay.utils';
const adaptResponse = (response)=>{
    var _response_canUndo;
    if (response.type === GenerateServiceDescriptionResponseType.Clarification) {
        return {
            canUndo: false,
            isClarification: true,
            conversationId: response.conversationId,
            output: null,
            clarificationText: response.message
        };
    }
    return {
        canUndo: response.type === GenerateServiceDescriptionResponseType.Message ? (_response_canUndo = response.canUndo) !== null && _response_canUndo !== void 0 ? _response_canUndo : false : false,
        isClarification: false,
        conversationId: response.conversationId,
        output: response.description,
        clarificationText: null
    };
};
const ServiceDescriptionRichTextRenderer = (param)=>{
    let { value } = param;
    const { logBI } = useStoreActions();
    const parsedRichText = useMemo(()=>convertHTMLToRichText(value), [
        value
    ]);
    const handleCopy = ()=>{
        if (!value) return;
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = value;
        const textCopied = tempDiv.textContent || tempDiv.innerText || '';
        logBI(17010, {
            textCopied
        });
    };
    return /*#__PURE__*/ _jsx("div", {
        onCopy: handleCopy,
        children: /*#__PURE__*/ _jsx(RichTextViewer, {
            value: parsedRichText,
            testId: SuggestedOutputDisplayTestIds.SuggestedDescription()
        })
    });
};
const renderServiceDescriptionOutput = (props)=>/*#__PURE__*/ _jsx(ServiceDescriptionRichTextRenderer, _object_spread({}, props));
const SERVICE_DESCRIPTION_BI_EVENT_IDS = {
    presetClick: {
        [PresetType.Formalize]: 17004,
        [PresetType.Polish]: 17005,
        [PresetType.Shorten]: 17006,
        [PresetType.Elaborate]: 17007
    },
    submitPrompt: 17009,
    undoClick: 17008,
    insertClick: 17002,
    replaceClick: 17003,
    closeClick: 17011
};
export const ServiceDescriptionHelpMeWriteDrawer = (param)=>{
    let { open, onClose, drawerWidth, serviceName, serviceDescription, serviceId, onInsert } = param;
    const { t: helpMeWriteTexts } = useLocaleKeys('helpMeWrite');
    const { t: suggestedTexts } = useLocaleKeys('helpMeWrite.suggestedDescription');
    const { t: textFieldTexts } = useLocaleKeys('helpMeWrite.textField');
    const { generateDescription } = useGenerateServiceDescription();
    const { undoDescription } = useUndoServiceDescription();
    const { acceptDescription } = useAcceptServiceDescription();
    const { rejectDescription } = useRejectServiceDescription();
    const { suggestions } = usePromptSuggestions();
    const hasExistingContent = !isEmptyRichTextString(serviceDescription);
    const htmlDescription = useMemo(()=>convertRichTextToHTML(serviceDescription, {
            templateVariableMode: 'inline'
        }), [
        serviceDescription
    ]);
    const originalVariableLabels = useMemo(()=>extractVariableLabelsFromDelta(serviceDescription), [
        serviceDescription
    ]);
    const presets = useMemo(()=>[
            {
                value: PresetType.Formalize,
                label: 'Formalize',
                biName: PresetType.Formalize
            },
            {
                value: PresetType.Polish,
                label: 'Polish',
                biName: PresetType.Polish
            },
            {
                value: PresetType.Elaborate,
                label: 'Elaborate',
                biName: PresetType.Elaborate
            },
            {
                value: PresetType.Shorten,
                label: 'Shorten',
                biName: PresetType.Shorten
            }
        ], []);
    const texts = useMemo(()=>({
            headerTitle: helpMeWriteTexts.title(),
            headerBeta: helpMeWriteTexts.beta(),
            promptQuestion: suggestedTexts.promptQuestion(),
            insertLabel: suggestedTexts.insert(),
            insertedLabel: suggestedTexts.inserted(),
            replaceLabel: suggestedTexts.replace(),
            replacedLabel: suggestedTexts.replaced(),
            undoLabel: suggestedTexts.undo(),
            placeholder: textFieldTexts.placeholder(),
            placeholderAfterStart: textFieldTexts.placeholder(),
            disclaimer: textFieldTexts.disclaimer(),
            errorMessage: suggestedTexts.errorMessage()
        }), [
        helpMeWriteTexts,
        suggestedTexts,
        textFieldTexts
    ]);
    const handleDrawerOpen = useCallback(async (setResponse)=>{
        if (serviceName == null || serviceName.trim() === '') return;
        if (hasExistingContent) return;
        const result = await generateDescription({
            requestType: GenerateServiceDescriptionType.Title,
            title: serviceName,
            serviceId
        });
        setResponse(adaptResponse(result));
    }, [
        serviceName,
        hasExistingContent,
        generateDescription,
        serviceId
    ]);
    const mutations = useMemo(()=>({
            onMessage: async (conversationId, prompt)=>{
                if (conversationId === '') {
                    if (prompt.kind === 'freeText') {
                        const result = await generateDescription({
                            requestType: GenerateServiceDescriptionType.Description,
                            description: htmlDescription,
                            message: prompt.text,
                            title: serviceName,
                            type: 'message',
                            serviceId
                        });
                        return adaptResponse(result);
                    }
                    const result = await generateDescription({
                        requestType: GenerateServiceDescriptionType.Description,
                        description: htmlDescription,
                        preset: prompt.preset,
                        title: serviceName,
                        type: 'preset',
                        serviceId
                    });
                    return adaptResponse(result);
                }
                if (prompt.kind === 'freeText') {
                    const result = await generateDescription({
                        requestType: GenerateServiceDescriptionType.Message,
                        message: prompt.text,
                        conversationId,
                        type: 'message',
                        serviceId
                    });
                    return adaptResponse(result);
                }
                const result = await generateDescription({
                    requestType: GenerateServiceDescriptionType.Preset,
                    preset: prompt.preset,
                    conversationId,
                    type: 'preset',
                    serviceId
                });
                return adaptResponse(result);
            },
            onUndo: async (conversationId)=>{
                const result = await undoDescription(conversationId);
                return adaptResponse(result);
            },
            onAccept: (conversationId, output)=>{
                if (onInsert) {
                    const parsedRichText = convertHTMLToRichText(output);
                    const withEmbeds = embedVariablePlaceholdersInDelta(parsedRichText, originalVariableLabels);
                    onInsert(withEmbeds);
                }
                acceptDescription(conversationId);
            },
            onReject: (conversationId)=>{
                rejectDescription(conversationId);
            }
        }), [
        generateDescription,
        undoDescription,
        acceptDescription,
        rejectDescription,
        onInsert,
        htmlDescription,
        serviceName,
        serviceId,
        originalVariableLabels
    ]);
    const renderFeedbackModal = useCallback((param)=>{
        let { conversationId, onClose: onModalClose } = param;
        return /*#__PURE__*/ _jsx(FeedbackModal, {
            conversationId: conversationId,
            onClose: onModalClose
        });
    }, []);
    return /*#__PURE__*/ _jsx(HelpMeWriteDrawer, {
        open: open,
        onClose: onClose,
        drawerWidth: drawerWidth,
        presets: presets,
        suggestions: suggestions,
        mutations: mutations,
        outputRenderer: renderServiceDescriptionOutput,
        biEventIds: SERVICE_DESCRIPTION_BI_EVENT_IDS,
        texts: texts,
        hasExistingContent: hasExistingContent,
        onDrawerOpen: handleDrawerOpen,
        renderFeedbackModal: renderFeedbackModal
    });
};
